import { FunctionComponent, memo, useMemo } from "react";
import { IProps } from "./tags-list.types";
import useTags from "hooks-for/tags/use-tags";
import useTagGroups from "hooks-for/tag-groups/use-tag-groups";
import { SpinnerUntil } from "@edgetier/components";
import { groupBy } from "@edgetier/utilities";
import { Accordion, AccordionItem, AccordionItemContent } from "@edgetier/client-components";
import TagGroupHeader from "./tag-group-header";
import TagsListItem from "./tags-list-item";
import { Permission, TagGroupType } from "@edgetier/watchtower-types";
import CreateTagGroupButton from "components-for/tags/create-tag-group-button";
import CreateTag from "./create-tag";
import TagsListContext from "./tags-list-context/tags-list-context";
import Page from "constants/page";
import "./tags-list.scss";
import classNames from "classnames";

/**
 * A list of tags and tag groups which can be edited.
 * @param props.tagGroupType        Only tags of this type would be shown
 * @param props.redirectRoute       Route used after a tag or tag group is removed
 * @param props.showExploreAction   Display a link item to Expore screen in tag actions
 * @param props.actionPermissions   Permissions required for managing tags
 */
const TagsList: FunctionComponent<IProps> = ({
    tagGroupType = TagGroupType.PhraseSearch,
    redirectRoute = Page.ManageTags,
    showExploreAction = true,
    actionPermissions = Permission.ManagePhraseTriggers,
    showTagGroupUnaryForAgents = false,
}) => {
    const { data: tags, isLoading: areTagsLoading } = useTags();
    const { data: tagGroups, isLoading: areTagGroupsLoading } = useTagGroups({
        tagGroupType,
    });
    const groupedTags = useMemo(() => groupBy(tags ?? [], ({ tagGroupId }) => tagGroupId), [tags]);

    return (
        <div className="tags-list">
            <TagsListContext.Provider
                value={{
                    tagGroupType,
                    redirectRoute,
                    showExploreAction,
                    actionPermissions,
                    showTagGroupUnaryForAgents,
                }}
            >
                <SpinnerUntil data={tags} isReady={!areTagsLoading && !areTagGroupsLoading}>
                    <div className="tags-list__accordion">
                        <Accordion>
                            {typeof tagGroups !== "undefined" ? (
                                tagGroups.map(({ tagGroupId, tagGroup }) => (
                                    <AccordionItem canOpen isOpen={false} key={tagGroupId}>
                                        <TagGroupHeader
                                            tagCount={groupedTags[tagGroupId]?.length ?? 0}
                                            tagGroup={tagGroup}
                                            tagGroupId={tagGroupId}
                                        />
                                        {Array.isArray(groupedTags[tagGroupId]) &&
                                            groupedTags[tagGroupId].length > 0 &&
                                            groupedTags[tagGroupId].map(({ tag, tagId }) => (
                                                <TagsListItem key={tagId} tag={tag} tagId={tagId} />
                                            ))}
                                        <AccordionItemContent>
                                            <CreateTag outline tagGroupId={tagGroupId} />
                                        </AccordionItemContent>
                                    </AccordionItem>
                                ))
                            ) : (
                                <div>Could not load tags</div>
                            )}
                        </Accordion>
                    </div>
                </SpinnerUntil>
                <div
                    className={classNames("tags-list__create-tag-group", {
                        "tags-list__create-tag-group--empty":
                            typeof tagGroups !== "undefined" && tagGroups.length === 0,
                    })}
                >
                    <CreateTagGroupButton />
                </div>
            </TagsListContext.Provider>
        </div>
    );
};

export default memo(TagsList);
